/**
 * Каркас: шапка в две строки и нижнее меню на телефоне.
 *
 * Прототип переключал режим вручную (демо-тумблер «Веб/Телефон»); здесь режим
 * выбирает медиа-запрос в app.css, а разметка одна — две копии одного экрана
 * неизбежно расходятся.
 *
 * Шапка собрана по образцу polymarket.com: первая строка — логотип, поиск и
 * действия, вторая — разделы с иконками, прокручиваемые вбок. Одной строки не
 * хватало: пять пунктов меню, поиск и четыре кнопки на 1040px переносились и
 * ломали вёрстку на ноутбуке.
 *
 * На телефоне та же шапка, но короче: логотип и одна главная кнопка в первой
 * строке, разделы — во второй, прокруткой вбок. Раньше шапки на телефоне не
 * было вовсе, и человек, открывший акцию по ссылке, не видел ни названия
 * платформы, ни способа уйти в ленту. Поиск в первой строке не повторяем: на
 * ленте он свой, прямо над фильтрами.
 *
 * Нижнее меню — четыре раздела и «Ещё», тоже как в референсе: остальное
 * уезжает в шторку. Семь иконок в ряд на 375px не читаются, а выбрасывать
 * разделы нельзя — у каждого свой экран.
 */
import { Avatar, Button, Icon, Logo, Sheet, SocialIcon, ThemeToggle, type IconName } from '@allswipes/ui';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { LANGS, LANG_NAMES, SOON_LANGS, useLang, useT } from '@allswipes/i18n';

import { SOCIALS, sitePage } from '../site';
import { useHasContests } from '../contests';
import { HowItWorks } from './HowItWorks';
import { SiteFooter } from './SiteFooter';
import { useSession } from '../session';

interface NavItem {
  to: string;
  icon: IconName;
  label: string;
  web: string;
  scan?: boolean;
  /** Пункта нет в нижнем меню телефона: он уезжает в шторку «Ещё». */
  webOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', icon: 'flame', label: 'Акции', web: 'Акции' },
  { to: '/live', icon: 'calendar', label: 'Эфиры', web: 'Эфиры' },
  // Архив — на вебе: в нижнем меню телефона шесть пунктов и так впритык, а
  // в подвале ссылка на архив есть на любом экране.
  { to: '/archive', icon: 'scroll', label: 'Архив', web: 'Архив', webOnly: true },
  { to: '/my', icon: 'ticket', label: 'Мои', web: 'Мои участия' },
  { to: '/scan', icon: 'scan', label: 'Скан', web: 'Сканировать', scan: true },
  { to: '/points', icon: 'coins', label: 'Баллы', web: 'Баллы', webOnly: true },
  { to: '/profile', icon: 'user', label: 'Профиль', web: 'Профиль', webOnly: true },
];

const CONTESTS: NavItem = {
  to: '/k',
  icon: 'star',
  label: 'Конкурсы',
  web: 'Конкурсы',
  webOnly: true,
};

export function Shell({
  children,
  wide,
  title,
  subtitle,
  action,
}: {
  children: ReactNode;
  wide?: boolean;
  title?: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  const { profile, signedIn } = useSession();
  const navigate = useNavigate();
  const { lang, setLang, t } = useLang();
  const [howOpen, setHowOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // Конкурсы появляются в меню только когда платформа их объявила.
  const hasContests = useHasContests();
  const nav = hasContests
    ? NAV.flatMap((n) => (n.to === '/live' ? [n, CONTESTS] : [n]))
    : NAV;

  return (
    <div className="shell">
      <header className="whead">
        <div className="inner">
          <NavLink to="/" className="brand" aria-label="ALLSWIPES">
            <Logo height={19} />
          </NavLink>

          <HeaderSearch />

          <button type="button" className="wghost webonly" onClick={() => setHowOpen(true)}>
            <Icon name="info" size={14} />
            <span className="t">{t('Как это работает')}</span>
          </button>

          {/* Язык меняет и интерфейс, и язык, на котором сервер отдаёт акции. */}
          <select
            className="wlang webonly"
            aria-label={t('Язык')}
            value={lang}
            onChange={(e) => setLang(e.target.value as (typeof LANGS)[number])}
          >
            {LANGS.map((l) => (
              <option key={l} value={l}>
                {LANG_NAMES[l]}
              </option>
            ))}
            {/* Языки, которых ещё нет, видно, но выбрать нельзя: половина
                интерфейса на чужом языке хуже, чем его отсутствие. */}
            <optgroup label={t('Скоро')}>
              {SOON_LANGS.map((l) => (
                <option key={l.code} value={l.code} disabled>
                  {l.name}
                </option>
              ))}
            </optgroup>
          </select>

          <span className="webonly">
            <ThemeToggle />
          </span>
          {/* Колокольчик на телефоне уезжает в «Ещё»: в строке из логотипа и
              двух кнопок он лишний, а непрочитанное видно и там. */}
          <span className="webonly">
            <NotificationsLink unread={signedIn ? undefined : 0} />
          </span>

          {signedIn && profile ? (
            <NavLink
              to="/profile"
              className="wme"
              title={`${profile.rank.name} · ${profile.points} баллов`}
            >
              <Avatar name={profile.user.display_handle || t('ты')} size={22} />
              <span className="mono">{profile.points}</span>
            </NavLink>
          ) : (
            <Button size="sm" onClick={() => navigate('/login')}>
              {t('Войти')}
            </Button>
          )}

          <Button variant="pri" size="sm" icon="scan" onClick={() => navigate('/scan')}>
            {t('Сканировать')}
          </Button>
        </div>

        <div className="wtabs">
          <nav className="inner wlinks">
            {nav.filter((n) => !n.scan).map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === '/'}>
                <Icon name={n.icon} size={14} />
                {t(n.web)}
              </NavLink>
            ))}
            <span className="sep" aria-hidden />
            {/* «О проекте» — это и есть публичная страница платформы: что
                это, кому и как устроена проверяемость. Открывается в той же
                вкладке: формально другой адрес, по ощущению — тот же продукт,
                и меню там такое же. */}
            <a href={sitePage('/about', lang)}>
              <Icon name="info" size={14} />
              {t('О проекте')}
            </a>
          </nav>
        </div>
      </header>

      <main className={`main ${wide ? 'wide' : ''}`}>
        {(title || action) && (
          <>
            <div className="page-title">
              {title && <h1 className="h1">{title}</h1>}
              <span className="spacer" />
              {action}
            </div>
            {subtitle && <p className="page-sub">{subtitle}</p>}
          </>
        )}
        {children}
      </main>

      {howOpen && <HowItWorks onClose={() => setHowOpen(false)} />}
      {moreOpen && (
        <MoreSheet
          nav={nav}
          onClose={() => setMoreOpen(false)}
          onHow={() => {
            setMoreOpen(false);
            setHowOpen(true);
          }}
        />
      )}

      <SiteFooter />

      <nav className="dock">
        {dockOrder(nav).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === '/'} className={n.scan ? 'scan' : undefined}>
            {n.scan ? (
              <span className="sc">
                <Icon name={n.icon} size={17} />
              </span>
            ) : (
              <Icon name={n.icon} size={17} />
            )}
            <span>{t(n.label)}</span>
          </NavLink>
        ))}
        {/* «Ещё» — не раздел, а шторка: кнопка, а не ссылка, и подсветки
            текущей страницы у неё нет. */}
        <button type="button" className="more" onClick={() => setMoreOpen(true)}>
          <Icon name="menu" size={17} />
          <span>{t('Ещё')}</span>
        </button>
      </nav>
    </div>
  );
}

/**
 * Поиск в шапке.
 *
 * Запрос живёт в адресе (`/?q=…`) — тот же параметр, что читает лента, поэтому
 * ссылку на результат можно отправить, а «назад» работает. На ленте поиск
 * применяется по ходу набора (через паузу), с других экранов — по Enter, чтобы
 * человека не уносило с его страницы на полуслове. Клавиша «/» фокусирует поле,
 * как на polymarket.com.
 */
function HeaderSearch() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onFeed = pathname === '/';
  const fromUrl = onFeed ? (params.get('q') ?? '') : '';
  const [value, setValue] = useState(fromUrl);
  const input = useRef<HTMLInputElement>(null);

  // Адрес — источник истины: сброс фильтров на ленте очищает и это поле.
  useEffect(() => setValue(fromUrl), [fromUrl]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = el && /^(INPUT|TEXTAREA)$/.test(el.tagName);
      if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const go = (q: string, replace: boolean) =>
    navigate(q.trim() ? `/?q=${encodeURIComponent(q.trim())}` : '/', { replace });

  useEffect(() => {
    if (!onFeed || value === fromUrl) return;
    const t = setTimeout(() => go(value, true), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, onFeed, fromUrl]);

  return (
    <form
      className="wsearch"
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        go(value, false);
        input.current?.blur();
      }}
    >
      <Icon name="search" size={14} />
      <input
        ref={input}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t('Поиск по акциям и брендам')}
        aria-label={t('Поиск по акциям и брендам')}
      />
      {value ? (
        <button type="button" className="clr" aria-label={t('Очистить')} onClick={() => setValue('')}>
          <Icon name="close" size={12} />
        </button>
      ) : (
        <kbd aria-hidden>/</kbd>
      )}
    </form>
  );
}

/** Колокольчик с точкой непрочитанного. */
function NotificationsLink({ unread }: { unread?: number }) {
  const t = useT();
  return (
    <NavLink to="/notifications" aria-label={t('Уведомления')} title={t('Уведомления')}>
      <span style={{ position: 'relative', display: 'inline-flex' }}>
        <Button variant="ghost" size="sm" icon="bell" title={t('Уведомления')} />
        {unread !== 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 3,
              right: 3,
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'var(--accent)',
              border: '1.5px solid var(--bg)',
            }}
          />
        )}
      </span>
    </NavLink>
  );
}

/**
 * Порядок нижнего меню: скан — посередине.
 *
 * Приподнятая акцентная кнопка держит центр ряда, как в прототипе; в списке
 * разделов она стоит там, где удобно читать меню на вебе, и эти два порядка
 * не обязаны совпадать.
 */
function dockOrder(nav: NavItem[]): NavItem[] {
  const items = nav.filter((n) => !n.webOnly);
  const scan = items.find((n) => n.scan);
  if (!scan) return items;
  const rest = items.filter((n) => !n.scan);
  const middle = Math.ceil(rest.length / 2);
  return [...rest.slice(0, middle), scan, ...rest.slice(middle)];
}

/**
 * Шторка «Ещё» нижнего меню.
 *
 * В нижнем меню помещается четыре раздела, а их у приложения вдвое больше.
 * Остальное живёт здесь — вместе с языком, темой, соцсетями и входом, как в
 * референсе: одно место, где человек находит всё, чего не видно на экране.
 *
 * Разделы берутся из того же списка NAV, что и меню: второй список пунктов
 * разошёлся бы с первым на ближайшей правке.
 */
function MoreSheet({
  nav,
  onClose,
  onHow,
}: {
  nav: NavItem[];
  onClose: () => void;
  onHow: () => void;
}) {
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const { profile, signedIn } = useSession();
  const rest = nav.filter((n) => n.webOnly);

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <Sheet title={t('Ещё')} onClose={onClose}>
      <div className="more-grid">
        {rest.map((n) => (
          <button key={n.to} type="button" className="more-item" onClick={() => go(n.to)}>
            <Icon name={n.icon} size={16} />
            <span>{t(n.web)}</span>
          </button>
        ))}
        <button type="button" className="more-item" onClick={() => go('/notifications')}>
          <Icon name="bell" size={16} />
          <span>{t('Уведомления')}</span>
        </button>
        <button type="button" className="more-item" onClick={onHow}>
          <Icon name="info" size={16} />
          <span>{t('Как это работает')}</span>
        </button>
        <a className="more-item" href={sitePage('/about', lang)}>
          <Icon name="shield" size={16} />
          <span>{t('О проекте')}</span>
        </a>
      </div>

      <div className="more-row">
        <span className="lab">{t('Язык')}</span>
        <select
          className="wlang"
          aria-label={t('Язык')}
          value={lang}
          onChange={(e) => setLang(e.target.value as (typeof LANGS)[number])}
        >
          {LANGS.map((l) => (
            <option key={l} value={l}>
              {LANG_NAMES[l]}
            </option>
          ))}
          <optgroup label={t('Скоро')}>
            {SOON_LANGS.map((l) => (
              <option key={l.code} value={l.code} disabled>
                {l.name}
              </option>
            ))}
          </optgroup>
        </select>
      </div>

      <div className="more-row">
        <span className="lab">{t('Тема')}</span>
        <ThemeToggle />
      </div>

      <div className="more-socials">
        {SOCIALS.map((s) => (
          <a key={s.name} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label}>
            <SocialIcon name={s.name} size={17} />
          </a>
        ))}
      </div>

      {signedIn && profile ? (
        <button type="button" className="more-me" onClick={() => go('/profile')}>
          <Avatar name={profile.user.display_handle || t('ты')} size={26} />
          <span className="n">{profile.user.display_handle || t('Профиль')}</span>
          <span className="mono">{profile.points}</span>
        </button>
      ) : (
        <Button variant="pri" block onClick={() => go('/login')}>
          {t('Войти')}
        </Button>
      )}
    </Sheet>
  );
}
